import { DrillScoring } from '@core';
import { computeHitFactor as hfFormula } from '../hit-factor.util';
import { RunViewModel, SessionDrillViewModel } from '../models';
import { RunLike, SessionDrillLike } from './run-like.types';

export const toSessionDrillViewModel = (input: SessionDrillLike): SessionDrillViewModel => {
  const scoring: DrillScoring = input.scoring as DrillScoring;
  const runs: readonly RunViewModel[] = input.runs.map(toRunViewModel);
  const bestId: string | null = findBestRunId(scoring, runs);
  const annotated: readonly RunViewModel[] = runs.map((r: RunViewModel) => ({ ...r, isBest: r.id === bestId }));
  const best: RunViewModel | null =
    bestId === null ? null : (annotated.find((r: RunViewModel) => r.id === bestId) ?? null);
  return {
    instanceId: input.instanceId ?? '',
    drillId: input.drillId,
    drillName: input.drillName,
    drillDescription: input.drillDescription ?? null,
    planNote: input.planNote ?? null,
    scoring,
    shotsCount: input.shotsCount,
    targetRuns: input.targetRuns,
    runs: annotated,
    best,
    addedInSession: input.addedInSession ?? false
  };
};

const toRunViewModel = (r: RunLike): RunViewModel => ({
  id: r.id,
  recordedAt: r.recordedAt instanceof Date ? r.recordedAt : new Date(r.recordedAt),
  reps: r.reps,
  timeSeconds: r.timeSeconds,
  parTimeSeconds: r.parTimeSeconds,
  hitsA: r.hitsA,
  hitsC: r.hitsC,
  hitsD: r.hitsD,
  hitsM: r.hitsM,
  hitsNS: r.hitsNS,
  hitFactor: computeHitFactor(r),
  isBest: false
});

const findBestRunId = (scoring: DrillScoring, runs: readonly RunViewModel[]): string | null => {
  if (runs.length === 0) return null;
  switch (scoring) {
    case DrillScoring.RepsOnly:
      return pickByMax(runs, (r: RunViewModel) => r.reps);
    case DrillScoring.TimePerRep:
      return pickByMin(runs, (r: RunViewModel) => r.timeSeconds);
    case DrillScoring.HitFactor:
      return pickByMax(runs, (r: RunViewModel) => r.hitFactor);
  }
};

const pickByMin = (runs: readonly RunViewModel[], getter: (r: RunViewModel) => number | null): string | null => {
  let bestId: string | null = null;
  let bestValue: number | null = null;
  for (const r of runs) {
    const v: number | null = getter(r);
    if (v === null) continue;
    if (bestValue === null || v < bestValue) {
      bestValue = v;
      bestId = r.id;
    }
  }
  return bestId;
};

const pickByMax = (runs: readonly RunViewModel[], getter: (r: RunViewModel) => number | null): string | null => {
  let bestId: string | null = null;
  let bestValue: number | null = null;
  for (const r of runs) {
    const v: number | null = getter(r);
    if (v === null) continue;
    if (bestValue === null || v > bestValue) {
      bestValue = v;
      bestId = r.id;
    }
  }
  return bestId;
};

const computeHitFactor = (r: RunLike): number | null =>
  hfFormula({
    hitsA: r.hitsA,
    hitsC: r.hitsC,
    hitsD: r.hitsD,
    hitsM: r.hitsM,
    hitsNS: r.hitsNS,
    timeSeconds: r.timeSeconds
  });
